import React from 'react';
import { MapPin, Home, Building2, Calculator, CheckCircle, Phone, Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import OrganizationSchema from '@/components/schema/OrganizationSchema';

const ServiceAreas = () => {
  const primaryAreas = [
    {
      name: 'Exeter',
      postcode: 'EX',
      description: 'Our head office is based in Exeter, making us the ideal choice for all domestic and commercial energy assessments across the city and surrounding villages.',
      services: ['Domestic EPCs', 'Commercial EPCs', 'Energy Advice', 'MEES Compliance'],
      icon: <Home className="w-6 h-6" />
    },
    {
      name: 'Devon',
      postcode: 'EX, TQ, PL',
      description: 'We cover the entire county of Devon, including Exeter, Plymouth, Torquay, Torbay, Newton Abbot, Teignmouth, Exmouth, Sidmouth, Honiton, Tiverton, Barnstaple, Bideford and all surrounding towns and villages.',
      services: ['Domestic EPCs', 'Commercial EPCs', 'Energy Advice', 'MEES Compliance'],
      icon: <MapPin className="w-6 h-6" />
    },
    {
      name: 'Cornwall',
      postcode: 'TR, PL',
      description: 'Full coverage across Cornwall including Truro, Newquay, Falmouth, Penzance, St Austell, Bodmin, Launceston, Camborne, Redruth and the Isles of Scilly.',
      services: ['Domestic EPCs', 'Commercial EPCs', 'Energy Advice', 'MEES Compliance'],
      icon: <MapPin className="w-6 h-6" />
    },
    {
      name: 'Plymouth',
      postcode: 'PL',
      description: 'Comprehensive energy assessment services across Plymouth, including city centre, suburbs and surrounding areas such as Ivybridge, Plympton, Plymstock and Saltash.',
      services: ['Domestic EPCs', 'Commercial EPCs', 'Energy Advice', 'MEES Compliance'],
      icon: <Building2 className="w-6 h-6" />
    }
  ];

  const secondaryAreas = [
    {
      name: 'Somerset',
      postcode: 'TA, BA',
      description: 'Taunton, Bridgwater, Yeovil, Frome, Glastonbury, Wells, Chard, Minehead, Wellington and surrounding areas.',
      coverage: 'Full on-site assessment coverage'
    },
    {
      name: 'Bristol',
      postcode: 'BS',
      description: 'Bristol city centre and greater Bristol area, including Clifton, Redland, Bedminster, Kingswood, Filton, Keynsham and surrounding suburbs.',
      coverage: 'Full on-site assessment coverage'
    },
    {
      name: 'Bath',
      postcode: 'BA',
      description: 'Bath city, North East Somerset, Keynsham, Midsomer Norton, Radstock and surrounding villages.',
      coverage: 'Full on-site assessment coverage'
    },
    {
      name: 'Dorset',
      postcode: 'DT, BH',
      description: 'Bournemouth, Poole, Weymouth, Dorchester, Bridport, Sherborne, Wimborne, Christchurch and surrounding areas.',
      coverage: 'Full on-site assessment coverage'
    },
    {
      name: 'Gloucester & Cheltenham',
      postcode: 'GL',
      description: 'Gloucester, Cheltenham, Stroud, Cirencester, Tewkesbury and the Cotswolds region.',
      coverage: 'Full on-site assessment coverage'
    }
  ];

  const nationwideServices = [
    {
      name: 'SAP Calculations',
      description: 'Full SAP calculations for new builds and extensions anywhere in the UK. Digital-only service—no site visit required.',
      price: 'From £180'
    },
    {
      name: 'U-Value Calculations',
      description: 'Thermal performance calculations for walls, roofs, floors and glazing. Nationwide digital service.',
      price: 'From £65 per element'
    },
    {
      name: 'Part O Calculations',
      description: 'Overheating risk assessments for Building Regulations compliance. Digital service available nationwide.',
      price: 'From £150'
    },
    {
      name: 'SBEM Calculations',
      description: 'Simplified Building Energy Model calculations for non-domestic buildings. Nationwide service—prices on request.',
      price: 'Prices on request'
    },
    {
      name: 'Thermal Bridge Calculations',
      description: 'Psi-value calculations for junctions and thermal bridging. Digital service available UK-wide.',
      price: 'Prices on request'
    },
    {
      name: 'Dynamic Thermal Modelling',
      description: 'Advanced overheating and thermal comfort analysis. Nationwide digital service.',
      price: 'Prices on request'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Service Areas | Exeter, Devon, Cornwall, Somerset, Bristol | Energy Performance Direct"
        description="Energy Performance Direct covers Exeter, Plymouth, Devon, Cornwall, Somerset, Bristol, Bath, Dorset, Gloucester and Cheltenham for on-site EPCs and energy assessments. Nationwide digital SAP calculations, U-values and Part O services available across the UK."
        keywords="EPC Exeter, EPC Devon, EPC Cornwall, EPC Plymouth, EPC Bristol, EPC Somerset, energy assessor South West, SAP calculations UK, nationwide energy services"
        canonical="/service-areas"
      />
      <OrganizationSchema />
      <Breadcrumbs items={[
        { name: 'Service Areas', path: '/service-areas' }
      ]} />
      <Navbar />

      {/* Header */}
      <section className="text-white py-16" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="service-areas-heading">
              Our Service Areas
            </h1>
            <p className="text-base sm:text-lg opacity-95">
              On-site assessments across the South West UK • Nationwide digital services
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-lg text-gray-700 mb-4">
                Energy Performance Direct is based in Exeter, Devon, and provides comprehensive energy assessment services across the South West of England. We offer on-site domestic and commercial EPCs, energy advice reports, and MEES compliance support throughout Devon, Cornwall, Somerset, Dorset, Bristol, Bath, Gloucester and Cheltenham.
              </p>
              <p className="text-base text-gray-600">
                For properties requiring digital-only services—such as SAP calculations, U-value calculations, Part O assessments, SBEM modelling, thermal bridge calculations and dynamic thermal modelling—we provide nationwide coverage across England, Wales, Scotland and Northern Ireland. These services do not require a site visit, allowing us to support projects anywhere in the UK with fast turnaround times and competitive pricing.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Home className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">On-Site Services</h3>
                      <p className="text-sm text-gray-600">South West UK</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">
                    Domestic EPCs, commercial EPCs, energy advice reports, MEES compliance assessments across Devon, Cornwall, Somerset, Dorset, Bristol, Bath, Gloucester and Cheltenham.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                      <Calculator className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">Digital Services</h3>
                      <p className="text-sm text-gray-600">Nationwide UK</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">
                    SAP calculations, U-values, Part O, SBEM, thermal bridge and dynamic thermal modelling available across England, Wales, Scotland and Northern Ireland.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Primary Coverage Areas */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Primary Coverage: Exeter & Devon</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {primaryAreas.map((area, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all" data-testid={`primary-area-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                        {area.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{area.name}</h3>
                        <p className="text-sm text-green-600 font-medium">Postcode: {area.postcode}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm mb-4">{area.description}</p>
                    <div className="border-t pt-4">
                      <p className="text-xs font-semibold text-gray-900 mb-2">Available Services:</p>
                      <div className="flex flex-wrap gap-2">
                        {area.services.map((service, idx) => (
                          <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Secondary Coverage Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Extended South West Coverage</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {secondaryAreas.map((area, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all" data-testid={`secondary-area-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <MapPin className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                      <div>
                        <h3 className="font-bold text-gray-900">{area.name}</h3>
                        <p className="text-xs text-gray-600">{area.postcode}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">{area.description}</p>
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-green-600 font-medium">{area.coverage}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-3xl mx-auto">
              <h4 className="font-bold text-amber-900 mb-2">Travel Surcharge</h4>
              <p className="text-sm text-amber-800">
                For properties located more than 1 hour drive from our Exeter office, a travel surcharge of £25 per hour applies. This helps cover fuel costs and travel time for our assessors. We'll always confirm total costs before booking.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nationwide Digital Services */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Nationwide Digital Services</h2>
            <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
              These services do not require a site visit. We can support projects anywhere in the UK—England, Wales, Scotland and Northern Ireland—with plans submitted digitally via email or upload.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {nationwideServices.map((service, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all" data-testid={`nationwide-service-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-bold text-gray-900 text-lg">{service.name}</h3>
                      <span className="text-sm font-bold text-cyan-600 whitespace-nowrap ml-2">{service.price}</span>
                    </div>
                    <p className="text-sm text-gray-700">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8 bg-cyan-50 border border-cyan-200 rounded-lg p-6 max-w-3xl mx-auto text-center">
              <h4 className="font-bold text-cyan-900 mb-2">How It Works</h4>
              <p className="text-sm text-cyan-800 mb-4">
                Email your plans, drawings, specifications or U-value build-up details to us. We'll review, quote, and deliver your calculations within 3-5 working days (or faster for urgent projects). No site visit needed—fast, accurate, nationwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center text-sm">
                <a href="mailto:web@energyperformancedirect.co.uk" className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-medium">
                  <Mail className="w-4 h-4" />
                  web@energyperformancedirect.co.uk
                </a>
                <span className="hidden sm:inline text-gray-400">|</span>
                <a href="tel:01392980077" className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-medium">
                  <Phone className="w-4 h-4" />
                  01392 980077
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Book Your Assessment?</h2>
            <p className="text-lg mb-8 opacity-95">
              Whether you need an on-site EPC in Exeter or nationwide SAP calculations, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/booking'}
                size="lg"
                className="bg-white text-green-600 hover:bg-gray-100 px-12 py-6 text-lg rounded-full shadow-lg"
                data-testid="service-areas-book-btn"
              >
                Book Now
              </Button>
              <Button 
                onClick={() => window.location.href = '/contact'}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-12 py-6 text-lg rounded-full"
                data-testid="service-areas-contact-btn"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceAreas;
